import React from 'react';

import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { useIntl } from 'react-intl';
import { useNavigate, useParams } from 'react-router-dom';

import { useDeleteValidatorAccount } from 'src/features/validatorAccounts/api/useDeleteValidatorAccount';
import { LIST_VALIDATOR_ACCOUNTS_RESOURCE } from 'src/features/validatorAccounts/api/useListValidatorAccounts';
import { validatorAccountsMessages } from 'src/features/validatorAccounts/utils/messages';
import { ValidatorAccount } from 'src/features/validatorAccounts/utils/types';
import { toast } from 'src/hooks/UseToast';
import { queryClient } from 'src/lib/react-query';
import { useParking } from 'src/providers/ParkingProvider';
import ConfirmationModal from 'src/registry/components/blocks/ConfirmationModal';
import { DataTableNewEntryProps } from 'src/registry/components/table';
import { generalMessages } from 'src/registry/utils/messages';
import { ICON_MAP } from 'src/utils/constants';
import { useOnError } from 'src/utils/errors';

interface DeleteValidatorAccountProps<TData> extends DataTableNewEntryProps<TData> {
    selectedAccount: ValidatorAccount;
    children?: React.ReactNode;
}

function DeleteValidatorAccount<TData>({ selectedAccount, children }: DeleteValidatorAccountProps<TData>) {
    const [isModalOpen, setIsModalOpen] = React.useState(false);
    const { formatMessage } = useIntl();
    const { parkingId } = useParking();
    const { accountId } = useParams();

    const navigate = useNavigate();

    const onError = useOnError();

    const { mutate: deleteEntity } = useDeleteValidatorAccount({
        onSuccess: () => {
            void queryClient.invalidateQueries({ queryKey: [LIST_VALIDATOR_ACCOUNTS_RESOURCE] });
            setIsModalOpen(false);
            toast({
                title: formatMessage(validatorAccountsMessages.deleteValidatorAccountSuccess),
                description: formatMessage(validatorAccountsMessages.deleteValidatorAccountSuccessDescription, {
                    accountName: selectedAccount.name,
                }),
            });
            if (accountId) {
                void navigate(-1);
            }
        },
        onError: (error) => {
            onError(error, () => null, { inlineErrorStatuses: [] });
        },
    });

    return (
        <ConfirmationModal
            isModalOpen={isModalOpen}
            setIsModalOpen={setIsModalOpen}
            dialogDetails={{
                title: {
                    ...validatorAccountsMessages.deleteValidatorAccount,
                },
                subTitle: {
                    ...validatorAccountsMessages.deleteValidatorAccountSubtitle,
                    values: {
                        accountName: selectedAccount.name,
                    },
                },
                icon: ICON_MAP.DELETE as IconProp,
                confirmationMessage: {
                    ...validatorAccountsMessages.confirmDeleteValidatorAccount,
                    values: {
                        accountName: selectedAccount.name,
                    },
                },
            }}
            callbackButton={{
                variant: 'destructiveLight',
                buttonTitle: generalMessages.delete,
                callbackFn: () => {
                    if (selectedAccount) {
                        return deleteEntity({
                            id: selectedAccount.id,
                            parkingId,
                        });
                    }
                },
            }}
        >
            {children}
        </ConfirmationModal>
    );
}

export default DeleteValidatorAccount;
